/**
 * 判断环境是否支持 localStorage / sessionStorage
 */
function safeStorage(type: 'localStorage' | 'sessionStorage') {
  try {
    if (typeof window === 'undefined') return null // SSR
    const storage = window[type]
    const testKey = '__et-test__'
    storage.setItem(testKey, '1')
    storage.getItem(testKey)
    storage.removeItem(testKey)
    return storage
  } catch {
    return null
  }
}

/**
 * 内存存储（降级用）
 */
class MemoryStorage {
  private data: Record<string, any> = {}

  getItem(key: string) {
    return this.data[key] ?? null
  }

  setItem(key: string, value: any) {
    this.data[key] = value
  }

  removeItem(key: string) {
    delete this.data[key]
  }
}

/**
 * Cookie 工具（不依赖第三方）
 */
const Cookie = {
  get(name: string) {
    if (typeof document === 'undefined') return null
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
    return match && match[2] ? decodeURIComponent(match[2]) : null
  },

  set(name: string, value: string, expireMs = 365 * 86400000, domain?: string) {
    if (typeof document === 'undefined') return

    const expires = new Date(Date.now() + expireMs).toUTCString()

    document.cookie = [
      `${name}=${encodeURIComponent(value)}`,
      `expires=${expires}`,
      'path=/',
      domain ? `domain=${domain}` : '',
    ]
      .filter(Boolean)
      .join('; ')
  },

  remove(name: string) {
    this.set(name, '', -1)
  },
}

/**
 * 支持 local / session / memory 自动降级的通用 Storage
 */
export class StorageWrapper {
  private storage: Storage | MemoryStorage
  private type: 'local' | 'session' | 'memory'

  constructor(type: 'local' | 'session' = 'local') {
    if (type === 'session' && safeStorage('sessionStorage')) {
      this.storage = safeStorage('sessionStorage')!
      this.type = 'session'
    } else if (type === 'local' && safeStorage('localStorage')) {
      this.storage = safeStorage('localStorage')!
      this.type = 'local'
    } else {
      this.storage = new MemoryStorage()
      this.type = 'memory'
    }
  }

  getItem<T = any>(key: string): T | null {
    try {
      const value = this.storage.getItem(key)
      if (!value) return null
      return JSON.parse(value)
    } catch {
      return null
    }
  }

  setItem(key: string, value: any) {
    try {
      this.storage.setItem(key, JSON.stringify(value))
    } catch {
      // 内存降级
      if (!(this.storage instanceof MemoryStorage)) {
        this.storage = new MemoryStorage()
        this.storage.setItem(key, JSON.stringify(value))
      }
    }
  }

  removeItem(key: string) {
    try {
      this.storage.removeItem(key)
    } catch {}
  }

  setCookie(key: string, value: any, expiresMs?: number, domain?: string) {
    Cookie.set(key, JSON.stringify(value), expiresMs, domain)
  }

  getCookie<T = any>(key: string): T | null {
    try {
      const value = Cookie.get(key)
      return value ? JSON.parse(value) : null
    } catch {
      return null
    }
  }

  removeCookie(key: string) {
    Cookie.remove(key)
  }
}
